import { Component, ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import {
  ILogger,
  IMetrics,
  Operation,
} from '@amzn/work-spaces-web-client-core-lib';
import { CoreFactory } from '@/bridge/factory/CoreFactory';
import GlobalErrorFallback from './GlobalErrorFallback';

interface ErrorBoundaryProps extends PropsWithChildren {
  /** Rendered when a descendant throws during render/lifecycle. */
  fallback?: ReactNode;
  /** Overridable for testing; defaults to the shared logger/metrics. */
  logger?: ILogger;
  metrics?: IMetrics;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level catch-all error boundary for the WorkSpaces Web Client.
 *
 * React render/lifecycle errors that are not caught by feature code would
 * otherwise unmount the whole tree and leave the user on a blank white page.
 * This boundary catches them, logs diagnostic information, emits an
 * unhandled-exception metric, and renders a graceful fallback instead.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private readonly logger: ILogger;
  private readonly metrics: IMetrics;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
    this.logger = props.logger ?? CoreFactory.getLogger();
    this.metrics = props.metrics ?? CoreFactory.getMetrics();
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.logger.fatal(
      'Unhandled error caught by top-level ErrorBoundary',
      error?.message,
      error?.stack ?? errorInfo?.componentStack ?? undefined
    );
    this.metrics.emitUnhandledExcpetion(Operation.Application, error);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? <GlobalErrorFallback />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
